import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Avatar, Space, Divider, notification, Select, Table, Popconfirm, InputNumber, Typography } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined, HomeOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { database, ref, update, get, set, remove } from '../../pages/Auth/db/firebase';
import { getCustomCurrencies, clearCurrencyCache } from '../../utils/currency';

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', position: 'before', decimals: 2 },
  { code: 'EUR', symbol: '€', name: 'Euro', position: 'before', decimals: 2 },
  { code: 'RWF', symbol: 'Rwf', name: 'Rwandan Franc', position: 'after', decimals: 0 }
];

const UserSettingsModal = ({ visible, onClose, userData, user }) => {
  const [form] = Form.useForm();
  const [currencyForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customCurrencies, setCustomCurrencies] = useState([]);
  const [currencyModalVisible, setCurrencyModalVisible] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  
  const [api, contextHolder] = notification.useNotification();
  
  const facilityId = userData?.facilityId;

  // Load custom currencies when modal opens
  useEffect(() => {
    const loadCustomCurrencies = async () => {
      if (visible && facilityId && facilityId !== 'me') {
        setLoadingCurrencies(true);
        try {
          const currencies = await getCustomCurrencies(facilityId);
          setCustomCurrencies(Object.values(currencies) || []);
        } catch (error) {
          console.error('Error loading custom currencies:', error);
        } finally {
          setLoadingCurrencies(false);
        }
      }
    };
    loadCustomCurrencies();
  }, [visible, facilityId]);

  React.useEffect(() => {
    if (visible && userData) {
      form.setFieldsValue({
        username: userData.username,
        email: userData.email || user?.email,
        phone: userData.phone,
        facilityId: userData.facilityId,
        role: userData.role,
        defaultCurrency: userData.defaultCurrency || 'USD',
      });
    }
  }, [visible, userData, user, form]);
  
  const getAllCurrencies = () => {
    return [...DEFAULT_CURRENCIES, ...customCurrencies];
  };
  
  const handleAddCurrency = () => {
    setEditingCurrency(null);
    currencyForm.resetFields();
    setCurrencyModalVisible(true);
  };

  const handleEditCurrency = (currency) => {
    setEditingCurrency(currency);
    currencyForm.setFieldsValue(currency);
    setCurrencyModalVisible(true);
  };

  const handleSaveCurrency = async (values) => {
    if (!facilityId || facilityId === 'me') {
      api.error({ message: 'Cannot add currencies', description: 'Custom currencies can only be added by facility admins.' });
      return;
    }
    
    try {
      const currencyCode = values.code.toUpperCase().trim();
      
      if (DEFAULT_CURRENCIES.some(c => c.code === currencyCode)) {
        api.error({ message: 'Invalid Currency Code', description: 'This currency code is already used by a default currency.' });
        return;
      }

      const currencyData = {
        code: currencyCode,
        symbol: values.symbol.trim(),
        name: values.name.trim(),
        position: values.position || 'before',
        decimals: values.decimals || 2
      };

      const currencyRef = ref(database, `facilities/${facilityId}/customCurrencies/${currencyCode}`);
      await set(currencyRef, currencyData);

      // Clear cache to force reload
      clearCurrencyCache(facilityId);

      // Reload custom currencies from database
      const customCurrenciesRef = ref(database, `facilities/${facilityId}/customCurrencies`);
      const currenciesSnapshot = await get(customCurrenciesRef);
      if (currenciesSnapshot.exists()) {
        const currencies = currenciesSnapshot.val();
        setCustomCurrencies(Object.values(currencies) || []);
      } else {
        setCustomCurrencies([]);
      }

      setCurrencyModalVisible(false);
      currencyForm.resetFields();
      api.success({ message: 'Currency Saved', description: `Custom currency ${currencyCode} has been ${editingCurrency ? 'updated' : 'added'} successfully.` });
    } catch (error) {
      api.error({ message: 'Failed to Save Currency', description: error.message });
    }
  };

  const handleDeleteCurrency = async (currencyCode) => {
    if (!facilityId || facilityId === 'me') return;
    
    try {
      const currencyRef = ref(database, `facilities/${facilityId}/customCurrencies/${currencyCode}`);
      await remove(currencyRef);
      
      // Clear cache to force reload
      clearCurrencyCache(facilityId);
      
      // Reload custom currencies from database
      const customCurrenciesRef = ref(database, `facilities/${facilityId}/customCurrencies`);
      const currenciesSnapshot = await get(customCurrenciesRef);
      if (currenciesSnapshot.exists()) {
        const currencies = currenciesSnapshot.val();
        setCustomCurrencies(Object.values(currencies) || []);
      } else {
        setCustomCurrencies([]);
      }
      
      api.success({ message: 'Currency Deleted', description: `Custom currency ${currencyCode} has been removed.` });
    } catch (error) {
      api.error({ message: 'Failed to Delete Currency', description: error.message });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Determine the correct path to update user data
      let updatePath;
      if (userData.facilityId === 'me' || userData.isSuperAdminVisiting) {
        updatePath = 'users/me';
      } else {
        updatePath = `users/${userData.facilityId}/${user.uid}`;
      }

      const updatedData = {
        username: values.username,
        phone: values.phone,
        defaultCurrency: values.defaultCurrency,
        updatedAt: new Date().toISOString(),
      };

      // Don't update email and role as they might require special handling
      await update(ref(database, updatePath), updatedData);

      api.success({
        message: 'Settings Updated',
        description: 'Your profile has been updated successfully.',
        placement: 'bottomRight',
      });

      onClose();
    } catch (error) {
      api.error({
        message: 'Update Failed',
        description: error.message,
        placement: 'bottomRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const displayName = userData?.username || user?.displayName || user?.email || 'User';
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <>
      {contextHolder}
      <Modal
        title="User Settings"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Avatar 
            size={80} 
            icon={<UserOutlined />}
            style={{ 
              backgroundColor: 'var(--primary-color)',
              color: 'var(--text-inverse)',
              marginBottom: 16
            }}
          >
            {initials}
          </Avatar>
          <h3 style={{ margin: 0, color: 'var(--lcolor)' }}>
            {displayName}
          </h3>
          <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
            {userData?.role} • {userData?.facilityId}
          </p>
        </div>

        <Divider />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="username"
            label={<span style={{ color: 'var(--lcolor)' }}>Username</span>}
            rules={[{ required: true, message: 'Please enter your username' }]}
          >
            <Input 
              prefix={<UserOutlined style={{ color: 'var(--text-tertiary)' }} />}
              placeholder="Enter username"
              style={{ color: 'var(--lcolor)' }}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={<span style={{ color: 'var(--lcolor)' }}>Email</span>}
          >
            <Input 
              prefix={<MailOutlined style={{ color: 'var(--text-tertiary)' }} />}
              placeholder="Email address"
              disabled
              style={{ color: 'var(--text-tertiary)' }}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label={<span style={{ color: 'var(--lcolor)' }}>Phone Number</span>}
            rules={[
              { required: true, message: 'Please enter your phone number' },
              { pattern: /^[+]?[\d\s\-()]+$/, message: 'Please enter a valid phone number' }
            ]}
          >
            <Input 
              prefix={<PhoneOutlined style={{ color: 'var(--text-tertiary)' }} />}
              placeholder="Enter phone number"
              style={{ color: 'var(--lcolor)' }}
            />
          </Form.Item>

          <Form.Item
            name="facilityId"
            label={<span style={{ color: 'var(--lcolor)' }}>Facility ID</span>}
          >
            <Input 
              prefix={<HomeOutlined style={{ color: 'var(--text-tertiary)' }} />}
              placeholder="Facility ID"
              disabled
              style={{ color: 'var(--text-tertiary)' }}
            />
          </Form.Item>

          <Form.Item
            name="role"
            label={<span style={{ color: 'var(--lcolor)' }}>Role</span>}
          >
            <Input 
              prefix={<IdcardOutlined style={{ color: 'var(--text-tertiary)' }} />}
              placeholder="User role"
              disabled
              style={{ color: 'var(--text-tertiary)' }}
            />
          </Form.Item>

          <Form.Item
            name="defaultCurrency"
            label={<span style={{ color: 'var(--lcolor)' }}>Default Currency</span>}
            rules={[{ required: true, message: 'Please select a default currency' }]}
          >
            <Select 
              placeholder="Select default currency"
              loading={loadingCurrencies}
              style={{ color: 'var(--lcolor)' }}
            >
              {getAllCurrencies().map(curr => (
                <Option key={curr.code} value={curr.code}>
                  <Space>
                    <span style={{ fontSize: '16px' }}>{curr.symbol}</span>
                    <span>{curr.name} ({curr.code})</span>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {facilityId && facilityId !== 'me' && (
            <>
              <Divider style={{ marginTop: '24px', marginBottom: '16px' }}>Custom Currencies</Divider>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <Text strong style={{ color: 'var(--lcolor)' }}>Manage Custom Currencies</Text>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAddCurrency}
                    size="small"
                  >
                    Add Custom Currency
                  </Button>
                </div>
                
                {customCurrencies.length > 0 ? (
                  <div style={{ overflowX: 'auto', marginTop: '8px', maxWidth: '100%' }}>
                    <Table
                      dataSource={customCurrencies}
                      rowKey="code"
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      style={{ minWidth: '600px' }}
                      columns={[
                        { 
                          title: <span style={{ color: 'var(--lcolor)' }}>Code</span>, 
                          dataIndex: 'code', 
                          key: 'code', 
                          width: 80,
                          render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span>
                        },
                        { 
                          title: <span style={{ color: 'var(--lcolor)' }}>Symbol</span>, 
                          dataIndex: 'symbol', 
                          key: 'symbol', 
                          width: 100,
                          render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span>
                        },
                        { 
                          title: <span style={{ color: 'var(--lcolor)' }}>Name</span>, 
                          dataIndex: 'name', 
                          key: 'name',
                          render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span>
                        },
                        { 
                          title: <span style={{ color: 'var(--lcolor)' }}>Position</span>, 
                          dataIndex: 'position', 
                          key: 'position', 
                          width: 100,
                          render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span>
                        },
                        { 
                          title: <span style={{ color: 'var(--lcolor)' }}>Decimals</span>, 
                          dataIndex: 'decimals', 
                          key: 'decimals', 
                          width: 80,
                          render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span>
                        },
                        {
                          title: <span style={{ color: 'var(--lcolor)' }}>Actions</span>,
                          key: 'actions',
                          width: 120,
                          fixed: 'right',
                          render: (_, record) => (
                            <Space>
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditCurrency(record)}
                                style={{ color: 'var(--lcolor)' }}
                              />
                              <Popconfirm
                                title="Delete Currency"
                                description={`Are you sure you want to delete ${record.code}?`}
                                onConfirm={() => handleDeleteCurrency(record.code)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                />
                              </Popconfirm>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: '12px', color: 'var(--lcolor)' }}>
                    No custom currencies added yet. Click "Add Custom Currency" to create one.
                  </Text>
                )}
              </div>
            </>
          )}

          <div style={{ 
            display: 'flex', 
            gap: 'var(--spacing-sm)', 
            justifyContent: 'flex-end',
            marginTop: 'var(--spacing-lg)'
          }}>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={loading}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
      
      {/* Custom Currency Modal */}
      <Modal
        title={editingCurrency ? 'Edit Custom Currency' : 'Add Custom Currency'}
        open={currencyModalVisible}
        onCancel={() => {
          setCurrencyModalVisible(false);
          currencyForm.resetFields();
          setEditingCurrency(null);
        }}
        onOk={() => currencyForm.submit()}
        okText={editingCurrency ? 'Update' : 'Add'}
        width={500}
      >
        <Form
          form={currencyForm}
          layout="vertical"
          onFinish={handleSaveCurrency}
          initialValues={{ position: 'before', decimals: 2 }}
        >
          <Form.Item
            name="code"
            label={<span style={{ color: 'var(--lcolor)' }}>Currency Code</span>}
            rules={[
              { required: true, message: 'Please enter currency code' },
              { pattern: /^[A-Z]{2,4}$/, message: 'Code must be 2-4 uppercase letters' }
            ]}
            extra="e.g., CAF, XAF, XOF (2-4 uppercase letters)"
          >
            <Input 
              placeholder="CAF" 
              maxLength={4}
              style={{ textTransform: 'uppercase', color: 'var(--lcolor)' }}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
          </Form.Item>

          <Form.Item
            name="symbol"
            label={<span style={{ color: 'var(--lcolor)' }}>Currency Symbol</span>}
            rules={[{ required: true, message: 'Please enter currency symbol' }]}
            extra="e.g., FCFA, ₦, ₹"
          >
            <Input placeholder="FCFA" maxLength={10} style={{ color: 'var(--lcolor)' }} />
          </Form.Item>

          <Form.Item
            name="name"
            label={<span style={{ color: 'var(--lcolor)' }}>Currency Name</span>}
            rules={[{ required: true, message: 'Please enter currency name' }]}
            extra="e.g., Central African Franc"
          >
            <Input placeholder="Central African Franc" style={{ color: 'var(--lcolor)' }} />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="position"
              label={<span style={{ color: 'var(--lcolor)' }}>Symbol Position</span>}
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select style={{ color: 'var(--lcolor)' }}>
                <Option value="before">Before amount (e.g., $100)</Option>
                <Option value="after">After amount (e.g., 100 Rwf)</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="decimals"
              label={<span style={{ color: 'var(--lcolor)' }}>Decimal Places</span>}
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={4} placeholder="2" style={{ width: '100%', color: 'var(--lcolor)' }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default UserSettingsModal;
